import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {number | undefined} */
  #checkoutTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {{quantity: number, summary: string | null, price: string | null, compare: string | null} | null} */
  #bundleDisplay = null;

  /** @type {string | null} */
  #defaultVariantText = null;

  /** @type {string | null} */
  #defaultPriceHTML = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(ThemeEvents.bundleUpdate, this.#handleBundleUpdate, { signal });

    // Warm the cart-count check while the buyer is still aiming at the bar
    // (hover/press/focus) so its round-trip overlaps the tap instead of
    // blocking the checkout redirect after it.
    this.addEventListener('pointerenter', this.#prefetchCartCount, { signal });
    this.addEventListener('pointerdown', this.#prefetchCartCount, { signal });
    this.addEventListener('focusin', this.#prefetchCartCount, { signal });

    // Back/forward cache: the bar's button freezes mid-"Redirecting…"
    // (loading + disabled) when the page unloads for checkout, and its 6s
    // failsafe timer freezes with it. Clean the state before the snapshot is
    // taken and again on restore — see buy-now.js.
    window.addEventListener('pagehide', this.#resetCheckoutLoading, { signal });
    window.addEventListener('pageshow', this.#resetCheckoutLoading, { signal });

    this.#getInitialQuantity();

    // The bundle selector may have dispatched its initial state before this
    // component upgraded — pull it directly once the bundle is defined.
    customElements.whenDefined('bundle-selector-component').then(() => {
      if (signal.aborted || this.#bundleDisplay) return;
      const scope = this.closest('.shopify-section') ?? document;
      const bundle = /** @type {any} */ (scope.querySelector('bundle-selector-component'));
      const display = bundle?.getStickyDisplay?.();
      if (display) {
        this.#bundleDisplay = display;
        this.#applyBundleDisplay();
      }
    });

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    clearTimeout(this.#checkoutTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
  }

  // Public action handlers
  /**
   * Handles the sticky button click. Desktop: express Buy now — checks out
   * exactly the current selection (single variant or mixed-color bundle),
   * clearing pre-existing lines first (see ProductFormComponent.requestCheckout).
   * Mobile: a plain add to cart that opens the cart drawer (morph), mirroring the
   * primary in-page mobile add (ProductFormComponent.handleSubmit) — no clear,
   * no checkout redirect. Never cross the wires: product-form's #leavesForCartPage
   * already gates leave-vs-drawer on the same >=750px breakpoint.
   */
  handleAddToCartClick = () => {
    const productForm = /** @type {any} */ (this.#getProductForm());
    if (!productForm) return;

    const { addToCartButton } = this.refs;
    if (addToCartButton.disabled) return;

    // Mirror the main add-to-cart button exactly (the one below the bundle).
    // handleSubmit self-gates on the >=750px breakpoint: desktop leaves for
    // /cart, mobile opens the drawer. No cart clear, no checkout redirect.
    if (typeof productForm.handleSubmit !== 'function') return;

    // Spin while the add lands: on desktop success the page navigates to /cart
    // (loading persists and is healed on pageshow/pagehide); a cartError resets
    // us via #handleCartAddComplete. A pure client-side reject emits no event,
    // so time out and recover rather than hang.
    addToCartButton.dataset.loading = 'true';
    addToCartButton.disabled = true;
    clearTimeout(this.#checkoutTimeout);
    this.#checkoutTimeout = setTimeout(() => {
      delete addToCartButton.dataset.loading;
      addToCartButton.disabled = false;
    }, 6000);

    productForm.handleSubmit(new Event('submit'));
  };

  #prefetchCartCount = () => {
    const productForm = /** @type {any} */ (this.#getProductForm());
    productForm?.prefetchCartCount?.();
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();

        // The morph replaced the bar contents with default variant markup —
        // recapture defaults and re-apply any active bundle display.
        this.#defaultVariantText = null;
        this.#defaultPriceHTML = null;
        this.#applyBundleDisplay();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Resets the "Redirecting…" state this component set on its button. Guarded
   * on data-loading so it can never blindly enable a button that's disabled
   * for another reason (unavailable variant) — loading implies it was enabled
   * when the click set it.
   */
  #resetCheckoutLoading = () => {
    clearTimeout(this.#checkoutTimeout);
    const { addToCartButton } = this.refs;
    if (addToCartButton?.dataset.loading !== 'true') return;
    delete addToCartButton.dataset.loading;
    addToCartButton.disabled = false;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Restore the sticky button if a Buy now add failed (on success the product
    // form navigates to checkout, so this branch never runs). Deferred until the
    // cart operation's promise settles rather than on initial dispatch.
    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(this.#resetCheckoutLoading);
    } else {
      this.#resetCheckoutLoading();
    }
  };

  /**
   * Handles bundle selector updates (tier or per-unit color changes)
   * @param {import('@theme/events').BundleUpdateEvent} event
   */
  #handleBundleUpdate = (event) => {
    this.#bundleDisplay = event.detail;
    this.#applyBundleDisplay();
  };

  /**
   * Mirrors the bundle selection in the bar: "1× Green / 1× Black" plus the
   * bundle total with the pre-discount total struck through. Restores the
   * default variant title and price when no multi-unit bundle is active.
   */
  #applyBundleDisplay() {
    const variantElement = /** @type {HTMLElement | null} */ (this.querySelector('.sticky-add-to-cart__variant'));
    const priceElement = /** @type {HTMLElement | null} */ (this.querySelector('.sticky-add-to-cart__price'));
    if (!variantElement || !priceElement) return;

    const display = this.#bundleDisplay;
    const active = Boolean(display && display.quantity >= 2 && display.price);

    if (!active) {
      if (this.#defaultVariantText !== null) variantElement.textContent = this.#defaultVariantText;
      if (this.#defaultPriceHTML !== null) priceElement.innerHTML = this.#defaultPriceHTML;
      this.#defaultVariantText = null;
      this.#defaultPriceHTML = null;
      return;
    }

    if (this.#defaultVariantText === null) this.#defaultVariantText = variantElement.textContent ?? '';
    if (this.#defaultPriceHTML === null) this.#defaultPriceHTML = priceElement.innerHTML;

    if (display?.summary) variantElement.textContent = display.summary;

    const priceSpan = document.createElement('span');
    priceSpan.className = 'price';
    priceSpan.textContent = display?.price ?? '';
    priceElement.replaceChildren(priceSpan);

    if (display?.compare) {
      const compareElement = document.createElement('s');
      compareElement.className = 'compare-at-price';
      compareElement.textContent = display.compare;
      priceElement.append(' ', compareElement);
    }
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
